/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import { useLocation } from "@reach/router";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

interface SEOProps {
  description?: string;
  lang?: string;
  meta?: Array<HTMLMetaElement>;
  title: string;
  /**
   * Absolute or site-relative URL of the image to show when a page is shared.
   * Defaults to the homepage hero shot.
   */
  image?: string;
}

const SEO: React.FC<SEOProps> = ({
  description,
  // Without this, <html> ships with no lang attribute at all, which screen
  // readers need in order to pick a pronunciation.
  lang = "en-GB",
  meta,
  title,
  image
}) => {
  const { site, prismicHomepageImage } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
            twitterUsername
          }
        }
        # Default social share image: Arthur's homepage hero at the 1.91:1
        # that Facebook, X, LinkedIn and WhatsApp all expect.
        #
        # The source is a tall 2:3 portrait, so a COVER crop to this ratio
        # slices out a band of forehead. CONTAIN letterboxes the whole
        # headshot onto the site's darkest palette colour instead, which
        # matches the photo's own backdrop.
        prismicHomepageImage {
          data {
            image {
              localFile {
                childImageSharp {
                  fixed(
                    width: 1200
                    height: 630
                    quality: 90
                    fit: CONTAIN
                    background: "#070c18"
                  ) {
                    src
                  }
                }
              }
            }
          }
        }
      }
    `
  );

  const {
    siteUrl,
    twitterUsername,
    title: siteTitle,
    description: siteDescription
  } = site.siteMetadata;

  const { pathname } = useLocation();

  const metaDescription = description || siteDescription;

  // Social crawlers ignore relative image and URL references, so everything
  // below is resolved against siteUrl.
  const toAbsolute = (path?: string) =>
    path && !path.startsWith("http") ? `${siteUrl}${path}` : path;

  const shareImage = toAbsolute(
    image ??
      prismicHomepageImage?.data?.image?.localFile?.childImageSharp?.fixed?.src
  );

  const canonicalUrl = toAbsolute(pathname || "/");

  const imageMeta = shareImage
    ? [
        { property: `og:image`, content: shareImage },
        { property: `og:image:width`, content: `1200` },
        { property: `og:image:height`, content: `630` },
        { name: `twitter:image`, content: shareImage },
        // Read out by screen readers in place of the card image
        {
          name: `twitter:image:alt`,
          content: `Arthur Bruce, Scottish baritone`
        }
      ]
    : [];

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={`%s | ${siteTitle}`}
      link={canonicalUrl ? [{ rel: `canonical`, href: canonicalUrl }] : []}
      meta={[
        {
          name: `description`,
          content: metaDescription
        },
        {
          property: `og:title`,
          content: title
        },
        {
          property: `og:description`,
          content: metaDescription
        },
        {
          property: `og:type`,
          content: `website`
        },
        {
          property: `og:site_name`,
          content: siteTitle
        },
        {
          property: `og:locale`,
          content: `en_GB`
        },
        ...(canonicalUrl
          ? [{ property: `og:url`, content: canonicalUrl }]
          : []),
        {
          // Upgraded from `summary`: with a share image present this renders
          // a full-width image card rather than a thumbnail.
          name: `twitter:card`,
          content: shareImage ? `summary_large_image` : `summary`
        },
        {
          // Arthur's handle, not the developer's - this is his portfolio
          name: `twitter:creator`,
          content: twitterUsername
        },
        {
          name: `twitter:site`,
          content: twitterUsername
        },
        {
          name: `twitter:title`,
          content: title
        },
        {
          name: `twitter:description`,
          content: metaDescription
        },
        ...imageMeta
      ].concat(meta || [])}
    />
  );
};

export default SEO;
